"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetEnergySavingData = void 0;
const react_1 = require("react");
const useGetEnergySavingChartData_1 = require("./useGetEnergySavingChartData");
const MAXIMUM_NUMBER_OF_RECENT_DAYS = 8;
const CURRENCY_SYMBOL = '$';
const PERCENTAGE_DECIMAL_PLACES = 2;
const AVERAGE_DECIMAL_PLACES = 2;
function extractLatestDailyCost(data) {
    const lastSample = data.at(-1);
    return lastSample && typeof lastSample.value === 'number'
        ? `${CURRENCY_SYMBOL}${lastSample.value.toFixed(2)}`
        : '';
}
function extractRecentAndPastCosts(data) {
    const recentCosts = data.slice(-MAXIMUM_NUMBER_OF_RECENT_DAYS);
    const pastCosts = data.slice(0, -MAXIMUM_NUMBER_OF_RECENT_DAYS);
    return { recentCosts, pastCosts };
}
function calculateAverageCost(costs) {
    const totalCost = costs.reduce((sum, item) => sum + item.value, 0);
    const numberOfDays = costs.length;
    return numberOfDays > 0 ? Number((totalCost / numberOfDays).toFixed(AVERAGE_DECIMAL_PLACES)) : 0;
}
function calculateWeekComparison(recentCosts, pastCosts) {
    const totalRecent = recentCosts.reduce((sum, item) => sum + item.value, 0);
    const totalPast = pastCosts.reduce((sum, item) => sum + item.value, 0);
    const recentAverage = recentCosts.length > 0 ? totalRecent / recentCosts.length : 0;
    const pastAverage = pastCosts.length > 0 ? totalPast / pastCosts.length : 0;
    if (pastAverage <= 0)
        return '';
    const change = ((recentAverage - pastAverage) / pastAverage) * 100;
    return `${change.toFixed(PERCENTAGE_DECIMAL_PLACES)}%`;
}
const useGetEnergySavingData = ({ historicalData, translate, daysToShow = 8, hubConfig, }) => {
    const { data: fullChartData } = (0, useGetEnergySavingChartData_1.useGetEnergySavingChartData)({
        historicalData,
        translate,
        daysToShow,
        hubConfig,
    });
    const [energySavingData, setEnergySavingData] = (0, react_1.useState)(undefined);
    const [value, setValue] = (0, react_1.useState)(0);
    const previousDataRef = (0, react_1.useRef)();
    const getEnergySavingData = (0, react_1.useCallback)(() => {
        if (!fullChartData || fullChartData.length === 0) {
            return;
        }
        const { recentCosts, pastCosts } = extractRecentAndPastCosts(fullChartData);
        const dailyCost = extractLatestDailyCost(recentCosts);
        const averageCost = calculateAverageCost(recentCosts);
        const lastWeekComparison = calculateWeekComparison(recentCosts, pastCosts);
        const newData = {
            dailyCost,
            averageCost,
            lastWeekComparison,
            dailyCosts: recentCosts,
        };
        // Note: Ugly hack to prevent from infinite re-render loop
        if (JSON.stringify(newData) !== JSON.stringify(previousDataRef.current)) {
            previousDataRef.current = newData;
            setEnergySavingData(newData);
        }
    }, [fullChartData]);
    const getEnergySavingCurrentState = (0, react_1.useCallback)(() => {
        if (!fullChartData || fullChartData.length === 0) {
            return;
        }
        const value = Number(fullChartData[fullChartData.length - 1]?.value?.toFixed(2));
        setValue(value || 0);
    }, [fullChartData]);
    (0, react_1.useEffect)(() => {
        getEnergySavingData();
        getEnergySavingCurrentState();
    }, [fullChartData]);
    return {
        data: energySavingData,
        value,
        getEnergySavingData,
        getEnergySavingCurrentState,
    };
};
exports.useGetEnergySavingData = useGetEnergySavingData;
